import React, { useState, useEffect, useCallback } from "react";
import CustomButton from '../../components/common/CustomButton';

import {
  Pane,
  Paragraph,
  Combobox,
  Button,
  Badge,
  AddIcon,
  Tooltip,
  HelpIcon,
  toaster,
  Text
} from 'evergreen-ui';

const CreatorBox = props => {
  const [addmode, setAddmode] = useState(false);
  const [stagedCreator, setStagedCreator] = useState(null);
  const [stagedCode, setStagedCode] = useState(null);
  const [formattedCreators, setFormattedCreators] = useState([]);
  const [formattedContributorCodes, setFormattedContributorCodes] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});

  // Format the array data for dropdown display
  const formatArrForDropdown = useCallback((arr, field) => {
    if (!arr || !Array.isArray(arr)) {
      return [];
    }

    const mappedArr = arr.map(item => {
      const newObj = { ...item };
      newObj.label = item[field];
      return newObj;
    });

    // Sort alphabetically by label
    mappedArr.sort((a, b) => {
      if (a.label < b.label) {
        return -1;
      }
      if (a.label > b.label) {
        return 1;
      }
      return 0;
    });

    return mappedArr;
  }, []);

  // Update formatted data whenever props change
  useEffect(() => {
    if (props.dataSourceForDropdown && Array.isArray(props.dataSourceForDropdown)) {
      const formatted = formatArrForDropdown(props.dataSourceForDropdown, "displayName");
      setFormattedCreators(formatted);
    }

    if (props.onixGlobalData?.onixcontributorcodes) {
      const formatted = formatArrForDropdown(props.onixGlobalData.onixcontributorcodes, "description");
      setFormattedContributorCodes(formatted);
    }
  }, [props.dataSourceForDropdown, props.onixGlobalData, formatArrForDropdown]);

  // Reset state when addmode changes to false
  useEffect(() => {
    if (!addmode) {
      setStagedCreator(null);
      setStagedCode(null);
      setValidationErrors({});
    }
  }, [addmode]);

  // Validate selections
  const validateSelections = useCallback(() => {
    const errors = {};
    if (!stagedCreator) errors.creator = "Creator is required";
    if (!stagedCode) errors.code = "Contributor code is required";

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [stagedCreator, stagedCode]);

  // Enhanced handler for creator selection
  const handleCreatorSelection = selected => {
    setStagedCreator(selected);

    // Clear validation error if it exists
    if (validationErrors.creator) {
      setValidationErrors(prev => ({...prev, creator: null}));
    }
  };

  // Enhanced handler for contributor code selection
  const handleCodeSelection = selected => {
    setStagedCode(selected);

    // Clear validation error if it exists
    if (validationErrors.code) {
      setValidationErrors(prev => ({...prev, code: null}));
    }
  };

  const handleAttachNewCreatorToProduct = () => {
    // Validate before submitting
    if (!validateSelections()) {
      return;
    }

    try {
      props.handleUpdateCreatorStaged(undefined, stagedCreator, stagedCode);
      toaster.success(`Added ${stagedCreator.displayName} as ${stagedCode.description}`);
      setAddmode(false);
      // State will be reset by the useEffect that triggers when addmode changes
    } catch (error) {
      toaster.danger('Failed to add creator. Please try again.');
    }
  };

  // Handle clicking the Add button or pressing Enter in a field
  const handleAddClick = (e) => {
    e.preventDefault();
    handleAttachNewCreatorToProduct();
  };

  const canAddNew = props.editing && props.editMode === "edit";
  const addNewPrompt = (props.productcreatorSet || []).length < 1 && props.editMode === "edit";

  const editPrompt = props.newMode
    ? ""
    : "(There are no creators attached to this product. Edit this record to add one.)";

  return (
    <Pane >
      <Paragraph size={300} fontWeight="bold" color="#5e5e5e" marginBottom={8}>CREATORS</Paragraph>

      {(props.productcreatorSet || []).map(creator => {
        let deletable = (props.royaltySet || []).filter(royalty => royalty.creator.id === creator.creator.id).length === 0
        let button = "";
        let creator_type = (<Badge>{creator.onixContributorCode.description}</Badge>);
        let creator_name = (<Paragraph size={300} marginRight={8}>{creator.creator.displayName}</Paragraph>);
        if (props.editing) {
          button =
            <Pane display="flex" alignItems="center">
              <Button
                size="small"
                disabled={!deletable}
                appearance="primary"
                intent="danger"
                onClick={e => props.handleDelete(creator.id)}
                marginLeft={8}
                marginTop={8}
              >
                Delete
              </Button>

              {!deletable &&
                <Tooltip content={
                  <Paragraph color="white" size={300}>Can't delete creator associated with royalty.</Paragraph>}
                  border="default">
                    <HelpIcon size={14} marginTop={6} color="muted" marginLeft={4} />
                </Tooltip>
              }
            </Pane>
        }
        return (
          <Pane key={`creator${creator.id}`} borderBottom="default" paddingBottom={8} marginBottom={8} display="flex" alignItems="center">
            {creator_name}
            {creator_type}
            {button}
          </Pane>
        )
      })}

      {addNewPrompt && (
        <Pane>
          <Paragraph fontStyle="italic" size={300}>
            {editPrompt}
          </Paragraph>
        </Pane>
      )}

      {canAddNew && !addmode && (
        <CustomButton
          iconBefore={AddIcon}
          onClick={() => setAddmode(true)}
        >
          Add Another Creator
        </CustomButton>
      )}

      {props.editing && addmode && (
        <Pane
          marginTop={8}
          padding={12}
          background="tint1"
          borderRadius={3}
        >
          <Pane display="flex" alignItems="flex-start">
            <Pane marginRight={8}>
              <Paragraph size={300} color="#667b7f" marginBottom={4}>Creator Name*</Paragraph>
              <Combobox
                items={formattedCreators}
                itemToString={item => (item ? item.label : '')}
                onChange={handleCreatorSelection}
                marginBottom={validationErrors.creator ? 2 : 8}
                width={200}
                openOnFocus
                placeholder="Select creator..."
                autocompleteProps={{
                  title: "Available creators",
                  isFilterDisabled: false
                }}
                selectedItem={stagedCreator}
              />
              {validationErrors.creator && (
                <Text size={300} color="danger" marginBottom={4}>
                  {validationErrors.creator}
                </Text>
              )}
            </Pane>

            <Pane marginRight={8}>
              <Paragraph size={300} color="#667b7f" marginBottom={4}>Creator Type*</Paragraph>
              <Combobox
                items={formattedContributorCodes}
                itemToString={item => (item ? item.label : '')}
                onChange={handleCodeSelection}
                marginBottom={validationErrors.code ? 2 : 8}
                width={200}
                openOnFocus
                placeholder="Select type..."
                autocompleteProps={{
                  title: "Creator types",
                  isFilterDisabled: false
                }}
                selectedItem={stagedCode}
              />
              {validationErrors.code && (
                <Text size={300} color="danger" marginBottom={4}>
                  {validationErrors.code}
                </Text>
              )}
            </Pane>
          </Pane>

          <Pane display="flex" marginTop={8}>
            <Button
              appearance="primary"
              size="small"
              iconBefore={AddIcon}
              onClick={handleAddClick}
              disabled={!stagedCreator || !stagedCode}
              marginRight={8}
            >
              Add Creator
            </Button>

            <Button
              size="small"
              onClick={() => setAddmode(false)}
            >
              Cancel
            </Button>
          </Pane>
        </Pane>
      )}
    </Pane>
  );
}

export default CreatorBox;
